/**
 * A die.
 *
 * The pips are `<i>` elements on a 3x3 grid, placed by `nth-child` rules in
 * styles.css — one block per face. Positioning them inline would be the obvious
 * approach and is exactly what the CSP drops on the floor.
 *
 * It renders as a `<button>` when it can be picked up and a `<span>` when it
 * cannot, rather than a button that is always there and sometimes disabled: a
 * die in the set-aside tray is not a control a screen reader should offer to
 * press, and a live die during someone else's turn is not yours to touch.
 */

import { cx } from '../ui/shell';

export type DieSize = 'lg' | 'sm';

interface Props {
  face: number;
  size?: DieSize;
  /** Chosen from the current throw. */
  held?: boolean;
  /** Locked from an earlier throw this turn. */
  set?: boolean;
  /** Live, but in no legal keep — dimmed so the dead dice read at a glance. */
  dead?: boolean;
  /**
   * Still in the cup. A die has to render as *something* before the first throw
   * of a turn, and rendering its stored face means six 1s — a 2,000-point hand —
   * sitting on the felt before anyone has thrown anything.
   */
  blank?: boolean;
  onClick?: () => void;
}

export function Die({ face, size = 'lg', held, set, dead, blank, onClick }: Props) {
  // No animation class here on purpose — the entry tumble belongs to the
  // `.die-slot` wrapper, so that it cannot overwrite `held`'s lift or `dead`'s
  // dimming through animation fill-mode. See the keyframes in styles.css.
  const className = cx(
    'die',
    `die--${size}`,
    !blank && `die--f${face}`,
    blank && 'die--blank',
    held && 'die--held',
    set && 'die--set',
    dead && !held && 'die--dead',
  );

  const pips = blank ? null : Array.from({ length: face }, (_, i) => <i key={i} />);
  const name = blank ? 'a die, not yet thrown' : `${face}${held ? ', set aside' : ''}`;

  if (!onClick) {
    return (
      <span className={className} role="img" aria-label={name}>
        {pips}
      </span>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={!!held} aria-label={name}>
      {pips}
    </button>
  );
}
